import { useMutation } from '@tanstack/react-query';
import { ValidationError } from 'yup';
import { MarketingCampaigns } from '@prisma/client';
import { PocketflowsCampaign } from '../../types/marketing';
import { finalizeCampaign } from '../../services/marketing';
import useGetMarketingData from './useGetMarketingData';
import useUser from '../global/useUser';
import { invalidateOffboardingCampaigns } from '../../services/offboardings';

interface Payload {
  campaign: MarketingCampaigns;
}

interface Options {
  onSuccess?: (campaign: PocketflowsCampaign) => void;
  onError?: (message?: string) => void;
  onMutate?: (variables: Payload) => void;
  onSettled?: () => void;
}

function useFinalizeCampaign({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  const { user } = useUser();
  const { data, isFetching } = useGetMarketingData();
  return useMutation({
    mutationFn: async ({ campaign }: Payload) => {
      if (!data?.id || isFetching || !user?.business?.id) return null;
      const result = await finalizeCampaign({
        campaignId: campaign.id,
        businessId: user.business.id,
        marketingBusinessId: data.id,
      });
      return result;
    },
    onSuccess: async result => {
      if (!result || 'error' in result)
        throw new Error(result?.error || 'Failed to finalize campaign');
      await invalidateOffboardingCampaigns();
      if (onSuccess) onSuccess(result.sourceCampaign);
    },
    onError: (err: unknown) => {
      if (err instanceof ValidationError) {
        onError?.(err.message);
        return;
      }
      if (err instanceof Error) {
        if (err.message === 'No customers found') {
          onError?.('No customers were found for the selected segments');
          return;
        }
        if (err.message === 'No payment method found') {
          onError?.(
            'You do not have a payment method set up. Visit the subscription page to add a payment method.',
          );
          return;
        }
        if (err.message === 'Invalid email') {
          onError?.(
            'The selected sender email address is no longer available.',
          );
          return;
        }
        if (err.message === 'Invalid custom domain') {
          onError?.(
            "Custom domain isn't ready to send yet. Check Custom Domain for setup or pending DNS.",
          );
          return;
        }
      }
      onError?.('Failed to finalize campaign');
    },
    onMutate,
    onSettled,
  });
}

export default useFinalizeCampaign;
